import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, Shield, Moon, Heart, ArrowRight, CheckCircle, Leaf, ShoppingCart, Percent } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const protocolHerbs = [
  {
    name: "Ashwagandha",
    handle: "ashwagandha",
    benefit: "Clinically shown to reduce cortisol by up to 30%. Supports stress resilience, energy, and hormonal balance.",
    dosage: "300-600mg standardized extract daily",
    icon: Shield
  },
  {
    name: "Maca Root",
    handle: "maca-root",
    benefit: "Peruvian adaptogen that supports energy, stamina, and libido without stimulant effects.",
    dosage: "1,500-3,000mg daily with food",
    icon: Zap
  },
  {
    name: "Rhodiola",
    handle: "rhodiola",
    benefit: "Enhances mental endurance, reduces fatigue under stress, and supports physical performance.",
    dosage: "200-400mg standardized extract daily",
    icon: Moon
  },
  {
    name: "Eleuthero",
    handle: "eleuthero",
    benefit: "Siberian adaptogen that improves stamina, immune function, and stress recovery.",
    dosage: "300-400mg daily",
    icon: Heart
  },
  {
    name: "Ginseng",
    handle: "ginseng",
    benefit: "Supports energy, cognitive function, and overall male vitality. Centuries of traditional use.",
    dosage: "200-400mg standardized extract daily",
    icon: Zap
  },
  {
    name: "Cordyceps",
    handle: "cordyceps",
    benefit: "Medicinal mushroom that supports oxygen utilization, endurance, and natural energy production.",
    dosage: "1,000-3,000mg daily",
    icon: Shield
  },
  {
    name: "Saw Palmetto",
    handle: "saw-palmetto",
    benefit: "Supports prostate health and healthy testosterone metabolism in men over 30.",
    dosage: "320mg standardized extract daily",
    icon: Heart
  }
];

const MensProtocol = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Zap className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="mens-protocol-title">
            Restore Confidence, Stamina & Control — Naturally
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            A science-backed herbal protocol designed for men experiencing the natural shifts that come after 30.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Understanding Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding What's Happening</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Performance shifts after 30 are common. Stress, recovery decline, and circulation changes all play a role. 
            These aren't signs of failure — they're signals that your body needs targeted support. The good news? 
            With the right natural approach, these patterns can be addressed effectively.
          </p>
        </div>

        {/* What This Protocol Supports */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">What This Protocol Supports</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Healthy Circulation</h3>
              <p className="text-gray-600">
                Support blood flow and vascular health with herbs traditionally used for cardiovascular wellness.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Stress Resilience</h3>
              <p className="text-gray-600">
                Adaptogenic herbs that help your body manage stress and maintain hormonal balance naturally.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Natural Stamina</h3>
              <p className="text-gray-600">
                Traditional botanicals known for supporting energy, endurance, and physical performance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Moon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Energy Consistency</h3>
              <p className="text-gray-600">
                Support steady energy levels throughout the day without crashes or artificial stimulants.
              </p>
            </div>
          </div>
        </div>

        {/* Protocol Herbs - Individual Buy Links */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Your Vitality Protocol Herbs</h2>
          <p className="text-gray-500 text-center mb-8">Each herb plays a specific role. Purchase individually or build your complete stack.</p>
          
          <div className="space-y-4" data-testid="protocol-herbs-list">
            {protocolHerbs.map((herb, idx) => {
              const IconComp = herb.icon;
              return (
                <div key={herb.name} className="bg-white rounded-xl shadow-md border border-gray-100 p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComp className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{herb.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{herb.benefit}</p>
                      <p className="text-xs text-blue-700 font-medium mt-1">Suggested: {herb.dosage}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/store?q=${encodeURIComponent(herb.name.split(" (")[0])}`)}
                    className="bg-blue-600 hover:bg-blue-700 rounded-full flex-shrink-0"
                    size="sm"
                    data-testid={`buy-${herb.handle}`}
                  >
                    <ShoppingCart className="mr-2 w-4 h-4" />
                    Buy Now
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center space-y-3">
            <Button
              onClick={() => { addItem("bundle:mens_vitality"); toast.success("Men's Vitality Protocol Bundle added to cart — 10% off!"); navigate("/cart"); }}
              className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-8 font-semibold shadow-lg"
              data-testid="add-mens-bundle-btn"
            >
              <Percent className="mr-2 w-4 h-4" />
              Add Complete Protocol Bundle — Save 10%
            </Button>
            <div>
            <Button
              onClick={() => navigate("/store")}
              variant="outline"
              className="rounded-full"
              data-testid="browse-all-herbs"
            >
              Browse All Herbs <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            </div>
          </div>
        </div>

        {/* Who This Is For */}
        <div className="bg-blue-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Who This Is For</h2>
          <p className="text-gray-700 text-lg mb-6">
            This protocol is designed for men experiencing early signs of performance decline who want proactive, 
            natural support. If you're noticing changes in your energy, stamina, or confidence — this is for you.
          </p>
          <ul className="space-y-3">
            {[
              "Men 30+ noticing subtle shifts in energy or performance",
              "Those seeking natural alternatives to support vitality",
              "Anyone wanting to be proactive about long-term wellness",
              "Men experiencing stress-related stamina changes"
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <Leaf className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl font-bold mb-4">Want a Personalized Plan?</h2>
          <p className="text-blue-100 mb-8 max-w-lg mx-auto">
            Get AI-powered herb recommendations tailored to your unique health profile, goals, and body type.
          </p>
          <Link to="/questionnaire">
            <Button
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-full"
              data-testid="get-personalized-plan"
            >
              Get My Personalized Plan <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link to="/mens-scorecard" className="text-blue-600 hover:text-blue-700 font-medium">
            &larr; Take the Vitality Assessment First
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MensProtocol;
